/**
 * Completing an older quotation's mandatory header in ONE write (Akhil, 2026-09-19).
 *
 * The Quotation doctype turned strict on 2026-09-17: company, customer, vessel, port, delivery
 * location, sales person, purchaser, customer reference and enquiry type are validated on EVERY
 * save. A quotation from before that, whose enquiry lacks the same fields, refuses each single-
 * field write on the header card for the OTHER missing fields — "not letting me update anything".
 * The heal in `useQuotation.ts` copies what the enquiry has; when the enquiry has nothing either,
 * only a write carrying everything at once can go.
 *
 * So while gaps remain the card holds the purchaser's entries as DRAFTS, and one Save writes them
 * together — to the quotation, and to the enquiry under its own field names, so the next document
 * built from that enquiry starts complete.
 */

import { QUOTATION_MANDATORY, type QuotationMandatoryField } from './quotationMandatory'
import type { UpdateEnquiryPatch } from './api/useEnquiryMutations'

export interface HeaderFacts {
  company: string
  customer: string
  vessel: string
  port: string
  delivery_location: string
  sales_person: string
  purchaser: string
  customer_reference_no: string
  currency: string
}

export interface HeaderContext {
  /** The enquiry's company — every document of the enquiry shares it. */
  company: string
  /** The enquiry's type — the quotation's Link copies it. */
  enquiryType: string
}

/** What the purchaser has typed but not yet saved, by quotation field name. */
export type HeaderDrafts = Partial<Record<'customer' | 'vessel' | 'port' | 'delivery_location' | 'sales_person' | 'purchaser' | 'customer_reference_no' | 'delivery_time' | 'customer_address' | 'order_type', string>>

const blank = (v: string | undefined | null) => !v || v.trim() === ''

export function quotationHeaderGaps(q: HeaderFacts, ctx: HeaderContext, drafts: HeaderDrafts): QuotationMandatoryField[] {
  const value: Record<QuotationMandatoryField, string | undefined> = {
    company: q.company || ctx.company,
    customer: drafts.customer ?? q.customer,
    vessel: drafts.vessel ?? q.vessel,
    port: drafts.port ?? q.port,
    delivery_location: drafts.delivery_location ?? q.delivery_location,
    sales_person: drafts.sales_person ?? q.sales_person,
    purchaser: drafts.purchaser ?? q.purchaser,
    customer_reference_no: drafts.customer_reference_no ?? q.customer_reference_no,
    customer_currency: q.currency,
    enquiry_type: ctx.enquiryType,
  }
  return QUOTATION_MANDATORY.filter((k) => blank(value[k]))
}

/** The two writes the Save makes. */
export function completionWrite(
  q: HeaderFacts,
  ctx: HeaderContext,
  drafts: HeaderDrafts,
): { quotation: Record<string, string>; enquiry: UpdateEnquiryPatch } {
  const quotation: Record<string, string> = {}
  for (const [k, v] of Object.entries(drafts)) if (v !== undefined) quotation[k] = v.trim()
  // The two the card cannot edit but the server insists on — supplied from the enquiry.
  if (blank(q.company) && !blank(ctx.company)) quotation.company = ctx.company
  if (!blank(ctx.enquiryType)) quotation.enquiry_type = ctx.enquiryType

  const enquiry: UpdateEnquiryPatch = {
    ...(drafts.customer !== undefined ? { customer: drafts.customer } : {}),
    ...(drafts.vessel !== undefined ? { vessel: drafts.vessel } : {}),
    ...(drafts.port !== undefined ? { delivery_port: drafts.port } : {}),
    ...(drafts.delivery_location !== undefined ? { delivery_location: drafts.delivery_location } : {}),
    ...(drafts.sales_person !== undefined ? { sales_person: drafts.sales_person } : {}),
    ...(drafts.customer_reference_no !== undefined ? { customer_reference_no: drafts.customer_reference_no.trim() } : {}),
  }
  return { quotation, enquiry }
}
